import { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Alert,
    Stack,
    LinearProgress,
    CircularProgress,
    Chip,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    TablePagination,
    Snackbar,
    Tooltip,
    InputAdornment,
} from '@mui/material';
import {
    CloudUpload as UploadIcon,
    Delete as DeleteIcon,
    Download as DownloadIcon,
    FilterList as FilterIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { useGetIdentity } from 'react-admin';
import { supabase } from '../providers/supabase/supabase';
import { usePapaParse } from '../misc/usePapaParse';
import { useIpaImport, IpaCsvRow } from './useIpaImport';

type IpaRow = {
    id: number;
    crop: string;
    class: string;
    ipa: number;
    state: string;
    aip: string;
    created_at: string;
};

type SortField = keyof Pick<IpaRow, 'crop' | 'class' | 'ipa' | 'state' | 'aip'>;

type SortDir = 'asc' | 'desc';

export const IpaManagerPage = () => {
    const { identity } = useGetIdentity();
    const isAdmin = !!(identity as any)?.administrator;

    const [rows, setRows] = useState<IpaRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [uploadOpen, setUploadOpen] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [sortField, setSortField] = useState<SortField>('crop');
    const [sortDir, setSortDir] = useState<SortDir>('asc');
    const [cropFilter, setCropFilter] = useState('');
    const [classFilter, setClassFilter] = useState('');
    const [stateFilter, setStateFilter] = useState('');
    const [aipFilter, setAipFilter] = useState('');
    const [snackMsg, setSnackMsg] = useState('');

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const PAGE_SIZE = 1000;
            let allData: IpaRow[] = [];
            let from = 0;
            let hasMore = true;

            while (hasMore) {
                const { data, error: fetchError } = await supabase
                    .from('ipa_manager')
                    .select('*')
                    .order('crop', { ascending: true })
                    .order('state', { ascending: true })
                    .range(from, from + PAGE_SIZE - 1);

                if (fetchError) throw fetchError;
                const pg = (data as IpaRow[]) ?? [];
                allData = allData.concat(pg);
                hasMore = pg.length === PAGE_SIZE;
                from += PAGE_SIZE;
            }

            setRows(allData);
        } catch (err: any) {
            console.error('Failed to fetch IPAs:', err);
            setError('Failed to load IPA data.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Unique values for filter dropdowns
    const uniqueCrops = useMemo(
        () =>
            Array.from(new Set(rows.map(r => r.crop)))
                .filter(Boolean)
                .sort(),
        [rows]
    );

    const uniqueClasses = useMemo(() => {
        const filtered = cropFilter
            ? rows.filter(r => r.crop === cropFilter)
            : rows;
        return Array.from(new Set(filtered.map(r => r.class)))
            .filter(Boolean)
            .sort();
    }, [rows, cropFilter]);

    const uniqueStates = useMemo(
        () =>
            Array.from(new Set(rows.map(r => r.state)))
                .filter(Boolean)
                .sort(),
        [rows]
    );

    const uniqueAips = useMemo(
        () =>
            Array.from(new Set(rows.map(r => r.aip)))
                .filter(Boolean)
                .sort(),
        [rows]
    );

    // Filtered + sorted rows
    const displayRows = useMemo(() => {
        let filtered = rows;
        if (cropFilter) {
            filtered = filtered.filter(r => r.crop === cropFilter);
        }
        if (classFilter) {
            filtered = filtered.filter(r => r.class === classFilter);
        }
        if (stateFilter) {
            filtered = filtered.filter(r => r.state === stateFilter);
        }
        if (aipFilter) {
            filtered = filtered.filter(r => r.aip === aipFilter);
        }

        return [...filtered].sort((a, b) => {
            const aVal = a[sortField];
            const bVal = b[sortField];

            if (aVal == null && bVal == null) return 0;
            if (aVal == null) return 1;
            if (bVal == null) return -1;

            let cmp: number;
            if (typeof aVal === 'number') {
                cmp = (aVal as number) - (bVal as number);
            } else {
                cmp = String(aVal).localeCompare(String(bVal));
            }

            return sortDir === 'asc' ? cmp : -cmp;
        });
    }, [
        rows,
        cropFilter,
        classFilter,
        stateFilter,
        aipFilter,
        sortField,
        sortDir,
    ]);

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortField(field);
            setSortDir('asc');
        }
    };

    const handleClearAll = async () => {
        if (
            !window.confirm(
                'Are you sure you want to delete ALL IPA records? This cannot be undone.'
            )
        )
            return;

        try {
            const { error: delError } = await supabase
                .from('ipa_manager')
                .delete()
                .neq('id', 0);
            if (delError) throw delError;
            setRows([]);
        } catch (err: any) {
            console.error('Failed to clear IPAs:', err);
            setError('Failed to clear IPA data.');
        }
    };

    const handleCellSave = async (id: number, field: string, value: any) => {
        const { error: updateErr } = await supabase
            .from('ipa_manager')
            .update({ [field]: value })
            .eq('id', id);

        if (updateErr) {
            console.error('Failed to update:', updateErr);
            setSnackMsg('Failed to save change.');
            return;
        }

        setRows(prev =>
            prev.map(r => (r.id === id ? { ...r, [field]: value } : r))
        );
        setSnackMsg('Saved');
    };

    const handleDownloadSample = () => {
        const csv =
            'Crop,Class,IPA,State,AIP\n' +
            'Wheat,016W,$150,MT,Great American\n' +
            'Barley,011B,$120,MT,NAU\n' +
            'Corn,041,$200,ID,RCIS\n';
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'ipa_manager_sample.csv';
        a.click();
        URL.revokeObjectURL(url);
    };

    const clearFilters = () => {
        setCropFilter('');
        setClassFilter('');
        setStateFilter('');
        setAipFilter('');
        setPage(0);
    };

    const hasActiveFilters =
        cropFilter || classFilter || stateFilter || aipFilter;

    const columns: {
        field: SortField;
        label: string;
        width?: number;
    }[] = [
        { field: 'crop', label: 'Crop' },
        { field: 'class', label: 'Class' },
        { field: 'ipa', label: 'IPA', width: 100 },
        { field: 'state', label: 'State', width: 80 },
        { field: 'aip', label: 'AIP' },
    ];

    return (
        <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
            >
                <Typography variant="h5" fontWeight={600}>
                    Insurance Per Acre (IPA)
                </Typography>
                {isAdmin && (
                    <Stack direction="row" spacing={1}>
                        <Tooltip title="Download sample CSV">
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<DownloadIcon />}
                                onClick={handleDownloadSample}
                            >
                                Sample CSV
                            </Button>
                        </Tooltip>
                        {rows.length > 0 && (
                            <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                startIcon={<DeleteIcon />}
                                onClick={handleClearAll}
                            >
                                Clear All
                            </Button>
                        )}
                        <Button
                            variant="contained"
                            startIcon={<UploadIcon />}
                            onClick={() => setUploadOpen(true)}
                        >
                            Upload CSV
                        </Button>
                    </Stack>
                )}
            </Stack>

            {/* Filter bar */}
            {rows.length > 0 && (
                <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    sx={{ mb: 2 }}
                    flexWrap="wrap"
                    useFlexGap
                >
                    <FilterIcon sx={{ color: 'text.secondary' }} />
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Crop</InputLabel>
                        <Select
                            value={cropFilter}
                            label="Crop"
                            onChange={e => {
                                setCropFilter(e.target.value as string);
                                setClassFilter('');
                                setPage(0);
                            }}
                        >
                            <MenuItem value="">
                                <em>All Crops</em>
                            </MenuItem>
                            {uniqueCrops.map(c => (
                                <MenuItem key={c} value={c}>
                                    {c}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Class</InputLabel>
                        <Select
                            value={classFilter}
                            label="Class"
                            onChange={e => {
                                setClassFilter(e.target.value as string);
                                setPage(0);
                            }}
                        >
                            <MenuItem value="">
                                <em>All Classes</em>
                            </MenuItem>
                            {uniqueClasses.map(c => (
                                <MenuItem key={c} value={c}>
                                    {c}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>State</InputLabel>
                        <Select
                            value={stateFilter}
                            label="State"
                            onChange={e => {
                                setStateFilter(e.target.value as string);
                                setPage(0);
                            }}
                        >
                            <MenuItem value="">
                                <em>All States</em>
                            </MenuItem>
                            {uniqueStates.map(s => (
                                <MenuItem key={s} value={s}>
                                    {s}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>AIP</InputLabel>
                        <Select
                            value={aipFilter}
                            label="AIP"
                            onChange={e => {
                                setAipFilter(e.target.value as string);
                                setPage(0);
                            }}
                        >
                            <MenuItem value="">
                                <em>All AIPs</em>
                            </MenuItem>
                            {uniqueAips.map(a => (
                                <MenuItem key={a} value={a}>
                                    {a}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {hasActiveFilters && (
                        <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                        >
                            {cropFilter && (
                                <Chip
                                    label={`Crop: ${cropFilter}`}
                                    onDelete={() => {
                                        setCropFilter('');
                                        setClassFilter('');
                                        setPage(0);
                                    }}
                                    size="small"
                                />
                            )}
                            {classFilter && (
                                <Chip
                                    label={`Class: ${classFilter}`}
                                    onDelete={() => {
                                        setClassFilter('');
                                        setPage(0);
                                    }}
                                    size="small"
                                />
                            )}
                            {stateFilter && (
                                <Chip
                                    label={`State: ${stateFilter}`}
                                    onDelete={() => {
                                        setStateFilter('');
                                        setPage(0);
                                    }}
                                    size="small"
                                />
                            )}
                            {aipFilter && (
                                <Chip
                                    label={`AIP: ${aipFilter}`}
                                    onDelete={() => {
                                        setAipFilter('');
                                        setPage(0);
                                    }}
                                    size="small"
                                />
                            )}
                            <Button size="small" onClick={clearFilters}>
                                Clear All
                            </Button>
                        </Stack>
                    )}
                    <Typography variant="body2" color="text.secondary">
                        {displayRows.length} record
                        {displayRows.length !== 1 ? 's' : ''}
                    </Typography>
                </Stack>
            )}

            {error && (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    onClose={() => setError('')}
                >
                    {error}
                </Alert>
            )}

            {loading ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        p: 6,
                    }}
                >
                    <CircularProgress />
                </Box>
            ) : rows.length === 0 ? (
                <Paper
                    sx={{
                        p: 6,
                        textAlign: 'center',
                        color: 'text.secondary',
                    }}
                >
                    <UploadIcon
                        sx={{
                            fontSize: 48,
                            mb: 2,
                            opacity: 0.5,
                        }}
                    />
                    <Typography variant="h6" gutterBottom>
                        No IPA data yet
                    </Typography>
                    {isAdmin ? (
                        <>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                                Upload a CSV file to populate the IPA table.
                            </Typography>
                            <Button
                                variant="contained"
                                startIcon={<UploadIcon />}
                                onClick={() => setUploadOpen(true)}
                            >
                                Upload CSV
                            </Button>
                        </>
                    ) : (
                        <Typography variant="body2">
                            An admin has not uploaded IPA data yet.
                        </Typography>
                    )}
                </Paper>
            ) : (
                <Paper sx={{ overflowX: 'auto' }}>
                    <TableContainer>
                        <Table size="small" sx={{ minWidth: 600 }}>
                            <TableHead>
                                <TableRow>
                                    {columns.map(col => (
                                        <TableCell
                                            key={col.field}
                                            sx={{
                                                fontWeight: 700,
                                                ...(col.width
                                                    ? {
                                                          width: col.width,
                                                      }
                                                    : {}),
                                            }}
                                            sortDirection={
                                                sortField === col.field
                                                    ? sortDir
                                                    : false
                                            }
                                        >
                                            <TableSortLabel
                                                active={sortField === col.field}
                                                direction={
                                                    sortField === col.field
                                                        ? sortDir
                                                        : 'asc'
                                                }
                                                onClick={() =>
                                                    handleSort(col.field)
                                                }
                                            >
                                                {col.label}
                                            </TableSortLabel>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {displayRows
                                    .slice(
                                        page * rowsPerPage,
                                        page * rowsPerPage + rowsPerPage
                                    )
                                    .map(row => (
                                        <EditableRow
                                            key={row.id}
                                            row={row}
                                            editable={isAdmin}
                                            onSave={handleCellSave}
                                        />
                                    ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={displayRows.length}
                        page={page}
                        onPageChange={(_, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={e => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                </Paper>
            )}

            <UploadDialog
                open={uploadOpen}
                onClose={() => setUploadOpen(false)}
                onComplete={() => {
                    setUploadOpen(false);
                    fetchData();
                }}
            />

            <Snackbar
                open={!!snackMsg}
                autoHideDuration={2000}
                onClose={() => setSnackMsg('')}
                message={snackMsg}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
            />
        </Box>
    );
};

// ---------- Editable Row ----------

function EditableRow({
    row,
    editable,
    onSave,
}: {
    row: IpaRow;
    editable: boolean;
    onSave: (id: number, field: string, value: any) => void;
}) {
    if (!editable) {
        return (
            <TableRow hover>
                <TableCell>{row.crop || '—'}</TableCell>
                <TableCell>{row.class || '—'}</TableCell>
                <TableCell>
                    {row.ipa != null ? `$${row.ipa.toLocaleString()}` : '—'}
                </TableCell>
                <TableCell>{row.state || '—'}</TableCell>
                <TableCell>{row.aip || '—'}</TableCell>
            </TableRow>
        );
    }

    return (
        <TableRow hover>
            <EditableTextCell
                value={row.crop}
                onSave={val => onSave(row.id, 'crop', val)}
            />
            <EditableTextCell
                value={row.class}
                onSave={val => onSave(row.id, 'class', val)}
            />
            <EditableCurrencyCell
                value={row.ipa}
                onSave={val => onSave(row.id, 'ipa', val)}
            />
            <EditableTextCell
                value={row.state}
                onSave={val => onSave(row.id, 'state', val)}
            />
            <EditableTextCell
                value={row.aip}
                onSave={val => onSave(row.id, 'aip', val)}
            />
        </TableRow>
    );
}

// ---------- Editable Cells ----------

function EditableTextCell({
    value,
    onSave,
}: {
    value: string;
    onSave: (val: string) => void;
}) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value);

    const commit = () => {
        setEditing(false);
        if (draft !== value) {
            onSave(draft);
        }
    };

    if (editing) {
        return (
            <TableCell sx={{ p: 0.5 }}>
                <TextField
                    value={draft}
                    onChange={e => setDraft(e.target.value)}
                    onBlur={commit}
                    onKeyDown={e => {
                        if (e.key === 'Enter') commit();
                        if (e.key === 'Escape') {
                            setDraft(value);
                            setEditing(false);
                        }
                    }}
                    size="small"
                    variant="standard"
                    autoFocus
                    fullWidth
                    sx={{
                        '& input': { fontSize: '0.875rem' },
                    }}
                />
            </TableCell>
        );
    }

    return (
        <TableCell
            onClick={() => {
                setDraft(value);
                setEditing(true);
            }}
            sx={{
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
            }}
        >
            {value || '—'}
        </TableCell>
    );
}

function EditableCurrencyCell({
    value,
    onSave,
}: {
    value: number;
    onSave: (val: number) => void;
}) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(String(value));

    const commit = () => {
        setEditing(false);
        const trimmed = draft.trim().replace(/[$,\s]/g, '');
        if (trimmed === '') return;
        const num = parseInt(trimmed, 10);
        if (!isNaN(num) && num !== value) {
            onSave(num);
        }
    };

    if (editing) {
        return (
            <TableCell sx={{ p: 0.5 }}>
                <TextField
                    value={draft}
                    onChange={e => setDraft(e.target.value)}
                    onBlur={commit}
                    onKeyDown={e => {
                        if (e.key === 'Enter') commit();
                        if (e.key === 'Escape') {
                            setDraft(String(value));
                            setEditing(false);
                        }
                    }}
                    size="small"
                    variant="standard"
                    autoFocus
                    fullWidth
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">$</InputAdornment>
                        ),
                    }}
                    sx={{
                        '& input': { fontSize: '0.875rem' },
                    }}
                />
            </TableCell>
        );
    }

    return (
        <TableCell
            onClick={() => {
                setDraft(String(value));
                setEditing(true);
            }}
            sx={{
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
            }}
        >
            {value != null ? `$${value.toLocaleString()}` : '—'}
        </TableCell>
    );
}

// ---------- Upload Dialog ----------

function UploadDialog({
    open,
    onClose,
    onComplete,
}: {
    open: boolean;
    onClose: () => void;
    onComplete: () => void;
}) {
    const { processBatch } = useIpaImport();
    const { importer, parseCsv, reset } = usePapaParse<IpaCsvRow>({
        batchSize: 50,
        processBatch,
    });

    const onDrop = useCallback(
        (accepted: File[]) => {
            if (accepted.length > 0) {
                parseCsv(accepted[0]);
            }
        },
        [parseCsv]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'text/csv': ['.csv'],
            'application/vnd.ms-excel': ['.csv'],
        },
        multiple: false,
        disabled: importer.state === 'parsing' || importer.state === 'running',
    });

    const handleClose = () => {
        if (importer.state === 'complete') {
            onComplete();
        } else {
            onClose();
        }
        reset();
    };

    const progress =
        importer.state === 'running' || importer.state === 'complete'
            ? importer.rowCount > 0
                ? (importer.importCount / importer.rowCount) * 100
                : 0
            : 0;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Upload IPA CSV</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <Alert severity="info">
                        Expected columns: <strong>Crop</strong>,{' '}
                        <strong>Class</strong>, <strong>IPA</strong> (e.g. $150
                        or 150), <strong>State</strong>, <strong>AIP</strong>
                    </Alert>

                    <Box
                        {...getRootProps()}
                        sx={{
                            border: '2px dashed',
                            borderColor: isDragActive ? 'primary.main' : '#ccc',
                            borderRadius: 2,
                            p: 4,
                            textAlign: 'center',
                            cursor:
                                importer.state === 'running'
                                    ? 'default'
                                    : 'pointer',
                            bgcolor: isDragActive
                                ? 'action.hover'
                                : 'transparent',
                            transition: 'all 0.2s',
                        }}
                    >
                        <input {...getInputProps()} />
                        <UploadIcon
                            sx={{
                                fontSize: 40,
                                color: 'text.secondary',
                                mb: 1,
                            }}
                        />
                        <Typography>
                            {isDragActive
                                ? 'Drop the CSV file here...'
                                : 'Drag & drop a CSV file here, or click to select'}
                        </Typography>
                    </Box>

                    {importer.state === 'parsing' && (
                        <Stack direction="row" spacing={1} alignItems="center">
                            <CircularProgress size={20} />
                            <Typography variant="body2">
                                Parsing CSV...
                            </Typography>
                        </Stack>
                    )}

                    {(importer.state === 'running' ||
                        importer.state === 'complete') && (
                        <Box>
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                sx={{ mb: 0.5 }}
                            >
                                <Typography variant="body2">
                                    {importer.state === 'complete'
                                        ? 'Upload complete!'
                                        : `Importing ${importer.importCount} of ${importer.rowCount} rows...`}
                                </Typography>
                                {importer.remainingTime != null && (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        ~
                                        {Math.ceil(
                                            importer.remainingTime / 1000
                                        )}
                                        s remaining
                                    </Typography>
                                )}
                            </Stack>
                            <LinearProgress
                                variant="determinate"
                                value={progress}
                                sx={{
                                    height: 8,
                                    borderRadius: 4,
                                }}
                            />
                            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                                <Typography
                                    variant="body2"
                                    color="success.main"
                                >
                                    ✓ {importer.importCount} imported
                                </Typography>
                                {importer.errorCount > 0 && (
                                    <Typography
                                        variant="body2"
                                        color="error.main"
                                    >
                                        ✗ {importer.errorCount} errors
                                    </Typography>
                                )}
                            </Stack>
                        </Box>
                    )}

                    {importer.state === 'error' && (
                        <Alert severity="error">
                            Failed to parse CSV: {importer.error.message}
                        </Alert>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>
                    {importer.state === 'complete' ? 'Done' : 'Cancel'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default IpaManagerPage;
